import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { CREW_RESOURCE } from '../crew/types';
import type { CrewQuote } from '../crew/CrewQuotePrompt';
import { DoneNotice, DraftCreatedError, FillChoiceDialog, type NoDraft } from './FillFromPacklist';
import { LINES_RESOURCE, QUOTES_KEY, QUOTES_RESOURCE, type VersionSummary } from './types';

/** What "Uzupełnij z obsady" answers (Obsada → kosztorys). */
/** `rulesNote` (review N4): the fill is done, but the services from the rules were not refreshed afterwards. */
export type CrewFillResult = { versionId: string; added: number; updated: number; removed: number; unchanged: number; noService: string[]; rulesNote?: string };

/** "Kosztorys uzupełniony z obsady (dodano 1, zmieniono 2, usunięto 1)." */
export function crewFillText(r: CrewFillResult): string {
  const parts = [`dodano ${r.added}`, `zmieniono ${r.updated}`];
  if (r.removed > 0) parts.push(`usunięto ${r.removed}`);
  return [`Kosztorys uzupełniony z obsady (${parts.join(', ')}).`, r.rulesNote ?? ''].filter(Boolean).join(' ');
}

/** "Funkcja Realizator MON nie ma usługi w cenniku — przypisz ją usłudze w Cenniku." (several: "Funkcje A, B nie mają…").
 * Owner 2026-10-24 („funkcja = usługa”): every function gets its service with it, so this is only a fallback
 * (e.g. a backfill that failed at the start). */
export function noServiceText(names: readonly string[]): string {
  if (names.length === 0) return '';
  return names.length === 1
    ? `Funkcja ${names[0]} nie ma usługi w cenniku — przypisz ją usłudze w Cenniku.`
    : `Funkcje ${names.join(', ')} nie mają usług w cenniku — przypisz je usługom w Cenniku.`;
}

/** The line fields "Uzupełnij z obsady" writes (the server checks them too). */
const CREW_FILL_FIELDS = ['quantity', 'daysMilli'] as const;

const noDraftOf = (error: unknown): NoDraft | null => (error instanceof ApiError && error.status === 409 && error.code === 'no_draft' ? ((error.current as NoDraft | undefined) ?? null) : null);

const CREW_FILL_TEXT = 'Usługi funkcji z obsady staną się takie jak obsada: ilość = liczba osób, dni = dni przydziału. Ceny, rabaty i opisy pozycji zostają; sprzęt i inne usługi też.';

/** A new draft from a frozen version, then filled from the crew (a draft made a moment ago is never overwritten unasked). */
async function newVersionFromCrew(n: NoDraft): Promise<CrewFillResult> {
  let draftId: string;
  try {
    draftId = (await api<{ id: string }>(`/api/quotes/quotes/${n.quoteId}/new-version`, { method: 'POST', body: { fromVersionId: n.versionId } })).id;
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 && error.code === 'draft_exists') {
      const d = error.current as { id: string; number: number };
      throw new DraftCreatedError({ id: d.id, number: d.number, state: 'robocza', outdated: false, sentAt: null });
    }
    throw error;
  }
  return api<CrewFillResult>(`/api/quotes/versions/${draftId}/from-crew`, { method: 'POST', body: {} });
}

/** Whether the role may compare the quote with the crew (the server checks the same). */
export function crewRights(me: Me): boolean {
  return can(me, CREW_RESOURCE, 'view') && fieldVisible(me, CREW_RESOURCE, 'functionId') && fieldVisible(me, LINES_RESOURCE, 'quantity');
}

/**
 * Obsada → kosztorys: the banner "Obsada różni się od kosztorysu." with "Uzupełnij z obsady" (the choice of #90:
 * overwrite the draft, or a new version of a sent one), and "Funkcja X nie ma usługi w cenniku." Renders nothing when
 * there is nothing to say.
 */
export function useQuoteCrewActions({
  crewQuote,
  me,
  quoteId,
  versions,
  phone,
  differs,
  noService,
  onNewDraft,
}: {
  /** Owner 18:30 (1): the crew's difference per function with „Dodaj” / „Zdejmij” (the same as in Obsada). */
  crewQuote?: Pick<CrewQuote, 'text' | 'buttons' | 'dialog'> & Partial<Pick<CrewQuote, 'fillCrew'>>;
  me: Me | undefined;
  quoteId: string | null;
  versions: VersionSummary[];
  phone: boolean;
  differs: boolean;
  noService: readonly string[];
  onNewDraft: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [choosing, setChoosing] = useState<VersionSummary | null>(null);
  const [notice, setNotice] = useState('');
  const fill = useMutation({
    mutationFn: (target: { draftId: string } | NoDraft) => ('draftId' in target ? api<CrewFillResult>(`/api/quotes/versions/${target.draftId}/from-crew`, { method: 'POST', body: {} }) : newVersionFromCrew(target)),
    onSuccess: (r) => {
      setChoosing(null);
      setNotice(crewFillText(r));
      onNewDraft();
    },
    onError: (error) => {
      // a draft made meanwhile: the choice again, with that draft (never overwritten unasked)
      if (error instanceof DraftCreatedError) return setChoosing(error.draft);
      // review I4 (as "Uzupełnij" of the packlist): sent in the meantime — the choice again, now with only a new version
      const n = noDraftOf(error);
      const v = n && versions.find((x) => x.id === n.versionId);
      if (v) setChoosing({ ...v, state: v.state === 'robocza' ? 'wyslana' : v.state });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  if (!me || !quoteId || versions.length === 0 || !crewRights(me)) return null;
  // review M8: and write what the server writes into a service line (rule 7a: a field one may see is one one may write)
  const mayEdit = can(me, QUOTES_RESOURCE, 'edit') && !phone && CREW_FILL_FIELDS.every((f) => fieldVisible(me, LINES_RESOURCE, f));
  const newest = versions.find((v) => v.state === 'robocza') ?? versions[versions.length - 1]!;
  // a 409 no_draft is not an error to show: the dialog changes instead
  const error = fill.error instanceof DraftCreatedError ? fill.error.message : fill.error instanceof ApiError ? (noDraftOf(fill.error) ? undefined : fill.error.message) : fill.error ? 'Nie udało się uzupełnić kosztorysu' : undefined;
  const missing = noServiceText(noService);
  return (
    <>
      {(differs || missing) && !choosing && (
        <div className="notice q-packlist-differs" role="status" data-feature-id="quotes.crew_sync">
          {differs && (
            <>
              {/* owner 22:02: one green bar — the crew's question (or the difference) and its buttons */}
              {crewQuote?.text || 'Obsada różni się od kosztorysu.'}{' '}
              {crewQuote?.buttons}
              {mayEdit && (
                <button
                  className="btn small"
                  type="button"
                  disabled={fill.isPending}
                  onClick={() => {
                    setNotice('');
                    fill.reset();
                    setChoosing(newest);
                  }}
                >
                  Uzupełnij z obsady
                </button>
              )}
              {/* owner 2026-10-31: the other way — Obsada from the quote (the quote stays as it is) */}
              {!phone && crewQuote?.fillCrew?.('Uzupełnij obsadę z kosztorysu')}
            </>
          )}
          {differs && missing && ' '}
          {missing && <span className="hint">{missing}</span>}
        </div>
      )}
      {crewQuote?.dialog}
      {notice && <DoneNotice text={notice} onClose={() => setNotice('')} featureId="quotes.crew_sync" />}
      {choosing && (
        <FillChoiceDialog
          newest={choosing}
          busy={fill.isPending}
          error={error}
          title="Uzupełnij kosztorys z obsady"
          text={CREW_FILL_TEXT}
          featureId="quotes.crew_sync"
          onOverwrite={() => fill.mutate({ draftId: choosing.id })}
          onNewVersion={() => fill.mutate({ quoteId, versionId: choosing.id })}
          onClose={() => setChoosing(null)}
        />
      )}
    </>
  );
}
